import {
  faCircleQuestion,
  faEdit,
  faNoteSticky,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AdvancedSelect,
  Button,
  Input,
  Loading,
  LoadingBlock,
  Table,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import { dissoc } from 'ramda'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import DeleteConfirmationModal from '../../components/DeleteConfirmationModal/DeleteConfirmationModal'
import FiltersModal from '../../components/FiltersModal/FiltersModal'
import HtmlModal from '../../components/HtmlModal/HtmlModal'
import InfiniteListWrapper from '../../components/InfiniteListWrapper/InfiniteListWrapper'
import { SortButton } from '../../components/SortButton/SortButton'
import { useDispatch, useStore } from '../../store/store'
import { TrainingTest } from '../../store/store.types'
import {
  abortTrainingTest,
  deleteTrainingTest,
  emptyTrainingTests,
  fetchTrainingTests,
  setTrainingTest,
} from '../../store/storeActions'
import { ProductType } from '../../types'
import { Sorting, SortingDirection } from '../../types/sorting'
import { locations } from '../../utils/locations'
import { serializeSorting } from '../../utils/sorting'
import { getTrainingStatusNames, toScore } from '../../utils/trainings'
import useStyles from './TrainingTestsList.styles'
import TrainingTestUpdate from './TrainingTestUpdate'

interface RouteParams {
  productId: string
  projectId: string
}

interface TrainingTestsListProps {
  trainingId: number
  productType?: ProductType
}

const itemsPerPage = 50

interface TrainingTestColumns extends TrainingTest {
  actions?: string
}

type TrainingTestFilters = {
  name?: string
  status?: string
}

const TrainingTestsList = ({
  trainingId,
  productType,
}: TrainingTestsListProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { trainingTests } = useStore()
  const dispatch = useDispatch()
  const history = useHistory()
  const { productId, projectId } = useParams<RouteParams>()
  const [isDeleteSubmitting, setDeleteSubmitting] = useState(false)
  const [isAbortSubmitting, setAbortSubmitting] = useState(false)
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [selectedTest, setSelectedTest] = useState<TrainingTestColumns>()
  const [trainingTestUpdateModal, setTrainingTestUpdateModal] = useState(false)
  const [htmlHelperModal, setHtmlHelperModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController>()

  const [sorting, setSorting] = useState<Sorting>({})
  const sortingRef = useRef<Sorting>({})
  const [testFilters, setTestFilters] = useState<TrainingTestFilters>({})
  const testFiltersRef = useRef<TrainingTestFilters>({})
  const [listKey, setListKey] = useState(0)

  const serializeFilters = (f: TrainingTestFilters): string => {
    const parts: string[] = []
    if (f.name) parts.push(`name=${encodeURIComponent(f.name)}`)
    if (f.status) parts.push(`status=${encodeURIComponent(f.status)}`)
    return parts.length ? `&${parts.join('&')}` : ''
  }

  const resetList = () => {
    abortControllerRef.current?.abort()
    setIsLoading(false)
    emptyTrainingTests(dispatch)
    setListKey((k) => k + 1)
  }

  const handleLoadMore = (page: number) => {
    if (isLoading) return
    setIsLoading(true)
    abortControllerRef.current = new AbortController()
    const orderingParam = serializeSorting(sortingRef.current) || undefined
    const filterParam = serializeFilters(testFiltersRef.current) || undefined
    fetchTrainingTests(
      dispatch,
      page !== 0,
      trainingId,
      itemsPerPage,
      page * itemsPerPage,
      orderingParam,
      filterParam,
      abortControllerRef.current.signal
    )?.finally(() => setIsLoading(false))
  }

  const handleSort = (key: string, direction: SortingDirection) => {
    const newSorting = { ...sortingRef.current, [key]: direction }
    sortingRef.current = newSorting
    setSorting(newSorting)
    resetList()
  }

  const handleSaveFilters = (f: Partial<TrainingTestFilters>) => {
    testFiltersRef.current = f as TrainingTestFilters
    setTestFilters(f as TrainingTestFilters)
    resetList()
  }

  const handleRemoveFilters = () => {
    testFiltersRef.current = {}
    setTestFilters({})
    resetList()
  }

  const filterLabels: { [key in keyof TrainingTestFilters]: string } = {
    name: t('Name'),
    status: t('Status'),
  }

  const statusOptions = [
    { value: 'IDLE', label: getTrainingStatusNames('IDLE') },
    { value: 'RUNNING', label: getTrainingStatusNames('RUNNING') },
    { value: 'COMPLETED', label: getTrainingStatusNames('COMPLETED') },
    { value: 'ERROR', label: getTrainingStatusNames('ERROR') },
    { value: 'CANCELED', label: getTrainingStatusNames('CANCELED') },
  ]

  useEffect(
    () => () => {
      abortControllerRef.current?.abort()
      emptyTrainingTests(dispatch)
    },
    [dispatch]
  )

  const columns: TableColumnInstance<
    TrainingTestColumns,
    keyof TrainingTestColumns
  >[] = [
    {
      id: 'id',
      header: t('Id'),
      template: ({ id }) => `#${id}`,
    },
    {
      id: 'name',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Name')}
            sorting={
              <SortButton
                sort={sorting?.name}
                onSort={(direction) => handleSort('name', direction)}
              />
            }
          />
        </div>
      ),
      template: useCallback(
        ({ name, notes }) => (
          <>
            {name}{' '}
            {notes && <FontAwesomeIcon icon={faNoteSticky} title={notes} />}
          </>
        ),
        []
      ),
    },
    {
      id: 'status',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Status')}
            sorting={
              <SortButton
                sort={sorting?.status}
                onSort={(direction) => handleSort('status', direction)}
              />
            }
          />
        </div>
      ),
      template: useCallback(
        ({ status }) => (
          <>
            {getTrainingStatusNames(status)}
            {status === 'RUNNING' && (
              <>
                {' '}
                <Loading size="0.9rem" />
              </>
            )}
          </>
        ),
        []
      ),
    },
    {
      id: 'overallPerformance',
      header: (
        <div>
          <TableColumnHeader
            title={t('Performance')}
            info={
              <FontAwesomeIcon
                icon={faCircleQuestion}
                onClick={() => setHtmlHelperModal(true)}
              />
            }
          />
        </div>
      ),
      template: ({ overallPerformance }) =>
        overallPerformance ? toScore(overallPerformance.data.value) : '-',
    },
    { id: 'createdBy', header: t('Created by') },
    {
      id: 'actions',
      header: t('Actions'),
      noWrap: true,
      align: 'right',
      width: 0,
      template: useCallback(
        (ts) => (
          <>
            {(ts.status === 'IDLE' || ts.status === 'RUNNING') && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  abortTrainingTest(dispatch, ts.id, setAbortSubmitting)
                }}
                disabled={isAbortSubmitting}
                label={t('Abort')}
                buttonType="button"
                type="default"
                size="sm"
              />
            )}

            <Button
              onClick={(e) => {
                e.stopPropagation()
                setSelectedTest(ts)
                setTrainingTestUpdateModal(true)
              }}
              label={t('Edit')}
              hideLabel
              icon={<FontAwesomeIcon icon={faEdit} />}
              buttonType="button"
              type="blank-default"
            />

            <Button
              onClick={(e) => {
                e.stopPropagation()
                setSelectedTest(ts)
                setDeleteConfirmationModal(true)
              }}
              label={t('Delete')}
              hideLabel
              icon={<FontAwesomeIcon icon={faTrash} />}
              disabled={
                isDeleteSubmitting ||
                ts.status === 'IDLE' ||
                ts.status === 'RUNNING'
              }
              buttonType="button"
              type="blank-destroy"
              size="sm"
            />
          </>
        ),
        [dispatch, isAbortSubmitting, isDeleteSubmitting, t]
      ),
    },
  ]

  return (
    <>
      <div className={classes.header}>
        <FiltersModal<TrainingTestFilters>
          initialValues={testFilters}
          labels={filterLabels}
          removeDisabled
          onSave={handleSaveFilters}
          onRemove={handleRemoveFilters}
        >
          {({ filters: f, setFilters }) => (
            <>
              <Input
                name="nameFilter"
                id="nameFilter"
                type="text"
                label={t('Name')}
                placeholder={t('Filter...')}
                value={f.name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilters((prev) => ({
                    ...prev,
                    name: e.target.value || undefined,
                  }))
                }
              />
              <AdvancedSelect
                name="statusFilter"
                id="statusFilter"
                isClearable
                label={t('Status')}
                placeholder={t('Filter...')}
                value={
                  f.status
                    ? statusOptions.find((o) => o.value === f.status) ?? null
                    : null
                }
                onChange={(v: { value: string; label: string } | null) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: v ? v.value : undefined,
                  }))
                }
                options={statusOptions}
              />
            </>
          )}
        </FiltersModal>
      </div>
      <InfiniteListWrapper key={trainingId}>
        {({ stickedHeader }) => (
          <InfiniteScroll
            key={listKey}
            pageStart={-1}
            loadMore={handleLoadMore}
            hasMore={
              trainingTests
                ? trainingTests.results.length < trainingTests.count &&
                  !isLoading
                : !isLoading
            }
            loader={
              <LoadingBlock message={t('Loading')} key="loading" compact />
            }
          >
            <Table
              columns={columns}
              data={trainingTests ? trainingTests.results : []}
              alternateRows
              stickyHeader
              stickedHeader={stickedHeader}
              compact
              onRowClick={(ts) => {
                if (!(isDeleteSubmitting || ts.status !== 'COMPLETED')) {
                  history.push(
                    locations.trainingTest(
                      Number(projectId),
                      Number(productId),
                      trainingId,
                      ts.id
                    )
                  )
                  setTrainingTest(dispatch, dissoc('actions', ts))
                }
              }}
              isEmpty={
                trainingTests &&
                trainingTests.count === 0 &&
                trainingTests.results.length <= 0
              }
              emptyMessage={t(
                `There's nothing here. Try creating a new {{ itemName }}.`,
                { itemName: t('test') }
              )}
            />
            {trainingTests &&
              trainingTests.results.length >= trainingTests.count &&
              trainingTests.count > 0 && (
                <div className={classes.endOfList}>
                  {t('All {{count}} records loaded', {
                    count: trainingTests.count,
                  })}
                </div>
              )}
          </InfiniteScroll>
        )}
      </InfiniteListWrapper>

      {deleteConfirmationModal && selectedTest && (
        <DeleteConfirmationModal
          onCloseRequest={() => setDeleteConfirmationModal(false)}
          onDeleteRequest={() =>
            deleteTrainingTest(dispatch, selectedTest.id, setDeleteSubmitting)
          }
        />
      )}

      {trainingTestUpdateModal && selectedTest && (
        <TrainingTestUpdate
          onClose={() => setTrainingTestUpdateModal(false)}
          onSuccess={() => {
            toast.success(t('The training has been edited'))
            setTrainingTestUpdateModal(false)
          }}
          trainingTestId={selectedTest.id}
          initialValues={{
            name: selectedTest.name,
            notes: selectedTest.notes || '',
          }}
        />
      )}
      {htmlHelperModal && productType && (
        <HtmlModal
          productType={productType}
          onCloseRequest={() => setHtmlHelperModal(false)}
        />
      )}
    </>
  )
}

export default TrainingTestsList
